import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { assetIdSignalFromRoute } from '../../../../../utils/asset-route.utils';
import { assetWriteSignal } from '../../../../../utils/asset-permissions.utils';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { assetSubResourceBreadcrumb } from '../../../../../utils/breadcrumb.utils';
import { MenuItem } from 'primeng/api';
import { MenuItemWithIcon } from '../../../../../types/menu.interface';
import { buildEntityMenuItems } from '../../../../../utils/entity-menu.utils';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { TagModule } from 'primeng/tag';
import { TabsModule } from 'primeng/tabs';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { HasPermissionDirective } from '../../../../../directives/has-permission.directive';
import { TableApiService } from '../../../../../services/api/table.service';
import { AuthService } from '../../../../../services/auth.service';
import { ToastService } from '../../../../../services/toast.service';
import { TableStore } from '../../../../../stores/table.store';
import { ConfirmationHelperService } from '../../../../../services/confirmation-helper.service';
import { ModalHelperService } from '../../../../../services/modal-helper.service';
import { PermissionService } from '../../../../../services/permission.service';
import { PermissionScope } from '../../../../../types/permission-scope.enum';
import { RolePermissionType } from '../../../../../types/role.interface';
import { CreateTableDialogComponent } from './create-table-dialog.component';
import { CesTable, TableMeasureRow } from '../../../../../types/table.interface';
import { CesMenuButtonSecondaryComponent } from '../../../../ui/ces-buttons/ces-menu-button-secondary.component';
import { ExpandableDescriptionComponent } from '../../../../ui/expandable-description/expandable-description.component';
import { CesAssetBreadcrumbComponent } from '../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesTitleComponent } from '../../../../ui/ces-route-title/ces-route-title.component';
import { CesStatusDotComponent } from '../../../../ui/ces-status-dot/ces-status-dot.component';
import { TableConfigTabComponent } from './tabs/table-config-tab.component';
import { TableDataTabComponent } from './tabs/table-data-tab.component';

type TabType = 'data' | 'config';

@Component({
  selector: 'ces-table-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    TagModule,
    TabsModule,
    ButtonModule,
    CesAssetBreadcrumbComponent,
    CesTitleComponent,
    CesMenuButtonSecondaryComponent,
    CesStatusDotComponent,
    ExpandableDescriptionComponent,
    HasPermissionDirective,
    TableConfigTabComponent,
    TableDataTabComponent,
  ],
  providers: [DialogService],
  templateUrl: './table-view.component.html',
  styleUrl: './table-view.component.scss',
})
export class TableViewComponent implements OnInit {
  protected readonly PermissionScope = PermissionScope;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslocoService);
  private readonly tableApiService = inject(TableApiService);
  private readonly dialogService = inject(DialogService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly modalHelper = inject(ModalHelperService);
  private readonly permissionService = inject(PermissionService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly tableStore = inject(TableStore);

  private readonly tabRoutes: TabType[] = ['data', 'config'];
  readonly activeIndex = signal<number>(0);
  readonly visitedTabs = signal<Set<number>>(new Set([0]));
  readonly isDirty = signal(false);
  readonly saved = signal(false);
  readonly tableMenuItems = computed<MenuItemWithIcon[]>(() => {
    if (!this.currentTable()) return [];
    return buildEntityMenuItems(
      this.translate,
      'tables.actions',
      {
        onExport: () => this.exportTrigger.update(v => v + 1),
        onEdit: () => this.editTable(),
        onDuplicate: () => {
          void this.duplicateTable();
        },
        onDelete: () => this.deleteTable(),
      },
      { canWrite: this.canWriteAsset() },
    );
  });
  readonly pendingMeasures = signal<TableMeasureRow[]>([]);
  readonly configVersion = signal(0);
  readonly hasSavedMeasures = signal(true);
  readonly exportTrigger = signal(0);

  readonly currentTableId = toSignal(this.route.paramMap.pipe(map(params => +(params.get('tableId') ?? '0'))), {
    initialValue: 0,
  });

  readonly currentAssetId = assetIdSignalFromRoute(this.route);
  private readonly canWriteAsset = assetWriteSignal(PermissionScope.Tables, this.currentAssetId);

  readonly currentTable = signal<CesTable | null>(null);

  private readonly translations = toSignal(this.translate.selectTranslation());

  readonly tableDescription = computed((): string | null => this.currentTable()?.description ?? null);

  readonly breadcrumbTrail = computed<MenuItem[]>(() => {
    this.translations();
    return assetSubResourceBreadcrumb(this.translate, {
      parentLabelKey: 'tables.breadcrumb',
      parentRouterLink: ['/assets', this.currentAssetId(), 'analysis', 'tables'],
      currentLabel: this.currentTable()?.name,
    });
  });

  readonly periodicityLabel = computed<string | null>(() => {
    this.translations();
    const table = this.currentTable();
    if (!table?.periodicity) return null;

    if (table.periodicity === 1) {
      return this.translate.translate('tables.periodicity.perMeasure');
    }
    if (table.periodicity === 2) {
      return this.translate.translate('tables.periodicity.perCampaign');
    }
    if (table.periodicity === 3 && table.periodicityValue && table.periodicityUnit) {
      const unitNames: Record<number, string> = {
        1: 'minute',
        2: 'hour',
        3: 'day',
        4: 'week',
        5: 'month',
        6: 'year',
      };
      const unitName = unitNames[table.periodicityUnit];
      const startingFrom = this.translate.translate('tables.periodicity.startingFrom');
      const startTime = (table.startTimePeriod ?? '00:00').substring(0, 5);
      if (table.periodicityValue === 1 && unitName) {
        const each = this.translate.translate(`tables.periodicity.each.${unitName}`);
        return `${each}, ${startingFrom} ${startTime}`;
      }
      const every = unitName ? this.translate.translate(`tables.periodicity.every.${unitName}`) : '';
      const intervalText = this.buildIntervalText(table.periodicityValue, table.periodicityUnit);
      return `${every} ${intervalText}, ${startingFrom} ${startTime}`;
    }
    return null;
  });

  private loadVersion = 0;

  constructor() {
    effect(() => {
      const tableId = this.currentTableId();
      if (tableId > 0) {
        const version = ++this.loadVersion;
        void this.loadTable(tableId, version);
      }
    });
  }

  ngOnInit() {
    const url = this.router.url;
    if (url.includes('/config')) {
      this.activeIndex.set(1);
      this.visitedTabs.update(tabs => new Set([...tabs, 1]));
    }
  }

  onTabChange(index: number): void {
    this.activeIndex.set(index);
    this.visitedTabs.update(tabs => new Set([...tabs, index]));
    const tableId = this.currentTableId();
    const assetId = this.currentAssetId();
    const tab = this.tabRoutes[index];
    void this.router.navigate([`/assets/${assetId}/analysis/tables/${tableId}/${tab}`], { replaceUrl: true });
  }

  onDirtyChange(dirty: boolean) {
    this.isDirty.set(dirty);
  }

  onMeasuresChange(measures: TableMeasureRow[]) {
    this.pendingMeasures.set(measures);
  }

  async onSave(measures: TableMeasureRow[]) {
    const tableId = this.currentTableId();
    if (!tableId) return;

    await this.tableApiService.saveTableMeasuresAsync(tableId, measures);
    this.isDirty.set(false);
    this.pendingMeasures.set([]);
    this.hasSavedMeasures.set(measures.length > 0);
    this.configVersion.update(v => v + 1);
    this.saved.set(true);
    setTimeout(() => this.saved.set(false));
  }

  private async loadTable(tableId: number, version: number): Promise<void> {
    try {
      const table = await this.tableApiService.getByIdAsync(tableId);
      if (version !== this.loadVersion) return;
      if (!table) {
        void this.router.navigate(['/404']);
        return;
      }
      this.currentTable.set(table);
      await this.handleInitialTabSelection(table);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        void this.router.navigate(['/404']);
      }
    }
  }

  private async handleInitialTabSelection(table: CesTable) {
    try {
      const measures = await firstValueFrom(this.tableApiService.getTableMeasures(table.id));
      this.hasSavedMeasures.set(measures.length > 0);
      if (measures.length > 0) return;
    } catch {
      return;
    }

    const assetId = this.currentAssetId();
    const userId = this.authService.currentUserId();
    if (!assetId || !userId) return;

    const hasWriteAccess = await this.permissionService.hasPermission(
      userId,
      assetId,
      PermissionScope.Tables,
      RolePermissionType.Write,
    );

    if (!hasWriteAccess) {
      void this.confirmationHelper
        .askInfo('tables.noMeasuresConfiguredReadOnly', 'tables.noMeasuresConfiguredTitle')
        .then(() => this.router.navigate([`/assets/${assetId}/analysis/tables`]));
      return;
    }

    const url = this.router.url;
    if (!url.includes('/config')) {
      this.activeIndex.set(1);
      this.visitedTabs.update(tabs => new Set([...tabs, 1]));
      void this.router.navigate([`/assets/${assetId}/analysis/tables/${table.id}/config`], { replaceUrl: true });
    }
  }

  private editTable() {
    const table = this.currentTable();
    if (!table) return;

    const dialogRef = this.dialogService.open(
      CreateTableDialogComponent,
      this.modalHelper.getFormConfig({
        header: this.translate.translate('tables.actions.edit'),
        data: {
          assetId: this.currentAssetId(),
          mode: 'edit',
          table,
        },
      }),
    );

    dialogRef.onClose.subscribe((result: Partial<CesTable> | undefined) => {
      if (result?.id === undefined) return;
      void this.applyEditTable(table, { ...result, id: result.id });
    });
  }

  private async applyEditTable(table: CesTable, result: Partial<CesTable> & { id: number }): Promise<void> {
    const periodicityChanged =
      result.periodicity !== table.periodicity ||
      result.periodicityValue !== table.periodicityValue ||
      result.periodicityUnit !== table.periodicityUnit ||
      result.startTimePeriod !== table.startTimePeriod;
    const assetId = this.currentAssetId() ?? undefined;
    const updated = await this.tableApiService.updateAsync(result.id, {
      ...result,
      ...(assetId !== undefined && { assetId }),
    });
    this.currentTable.set(updated);
    if (periodicityChanged) {
      this.configVersion.update(v => v + 1);
    }
  }

  private async duplicateTable() {
    const table = this.currentTable();
    const assetId = this.currentAssetId();
    if (!table || !assetId) return;

    await this.tableStore.loadByAssetId(assetId);
    const existingTableNames = this.tableStore.tablesByAssetId(assetId).map((t: CesTable) => t.name);

    const dialogRef = this.dialogService.open(
      CreateTableDialogComponent,
      this.modalHelper.getFormConfig({
        header: this.translate.translate('tables.actions.duplicate'),
        data: {
          mode: 'duplicate',
          table,
          existingTableNames,
        },
      }),
    );

    dialogRef.onClose.subscribe((result: Partial<CesTable> | undefined) => {
      if (!result) return;
      void this.applyDuplicateTable(table, assetId, result);
    });
  }

  private async applyDuplicateTable(table: CesTable, assetId: number, result: Partial<CesTable>): Promise<void> {
    try {
      const created = await this.tableApiService.createAsync({
        ...result,
        assetId,
        metadata: table.metadata,
      } as Omit<CesTable, 'id'>);
      await this.tableApiService.copyTableMeasuresAsync(table.id, created.id);
      this.tableStore.invalidateCache();
      void this.router.navigate(['/assets', assetId, 'analysis', 'tables', created.id]);
    } catch {
      this.toastService.error(this.translate.translate('common.toasts.operationFailed'));
    }
  }

  private buildIntervalText(value: number, unit: number): string {
    const intervalKeyBuilders: Record<number, (v: number) => string> = {
      1: v => `${v}min`,
      2: v => `${v}h`,
      3: v => `${v}day`,
      4: v => `${v}week`,
      5: v => (v === 1 ? '1month' : `${v}months`),
      6: v => `${v}year`,
    };
    const key = intervalKeyBuilders[unit]?.(value);
    if (key) {
      const label = this.translate.translate(`tables.periodicity.interval.${key}`);
      if (label.includes(' - ')) return label.substring(label.lastIndexOf(' - ') + 3);
      return label;
    }
    return `${value}`;
  }

  private deleteTable(): void {
    const table = this.currentTable();
    if (!table) return;

    this.confirmationHelper.confirmDelete(table.name, 'tables.confirmDeleteMessage', () => {
      void this.applyDeleteTable(table);
    });
  }

  private async applyDeleteTable(table: CesTable): Promise<void> {
    await this.tableApiService.deleteAsync(table.id);
    void this.router.navigate([`/assets/${this.currentAssetId()}/analysis/tables`]);
  }
}
